import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Logo } from './Logo';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const mainNavItems = [
    { key: 'product', path: 'product' },
    { key: 'howItWorks', path: 'how-it-works' },
    { key: 'pricing', path: 'pricing' },
  ];

  const contentNavItems = [
    { key: 'benchmarks', path: 'benchmarks' },
    { key: 'blog', path: 'blog' },
  ];

  const moreNavItems = [
    { key: 'about', path: 'about' },
    { key: 'help', href: 'https://drive.processsurge.com/published/help/Welcome.md' },
    { key: 'status', href: 'https://status.processsurge.com/status/main' },
  ];

  const isActive = (path) => {
    const currentPath = location.pathname.replace(`/${language}`, '').replace(/^\//, '');
    if (path === '' && currentPath === '') return true;
    if (path !== '' && currentPath.startsWith(path)) return true;
    return false;
  };

  const navLinkClass = (active) =>
    `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'text-cyan-400 bg-cyan-400/10'
        : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
    }`;

  const dropdownTriggerClass = (active) =>
    `flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'text-cyan-400 bg-cyan-400/10'
        : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Logo className="h-[40px] lg:h-[40px] w-auto" linkClassName="-mt-0.5" />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {mainNavItems.map((item) => (
              <Link
                key={item.key}
                to={`/${language}/${item.path}`}
                className={navLinkClass(isActive(item.path))}
                data-testid={`nav-${item.key}`}
              >
                {t.nav[item.key]}
              </Link>
            ))}

            {/* Content dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={dropdownTriggerClass(contentNavItems.some((i) => isActive(i.path)))}
                  data-testid="nav-content"
                  type="button"
                >
                  {t.nav.content ?? (language === 'pt' ? 'Conteúdo' : 'Content')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-slate-950 border-slate-800 text-slate-100"
              >
                {contentNavItems.map((item) => (
                  <DropdownMenuItem key={item.key} asChild className="cursor-pointer">
                    <Link to={`/${language}/${item.path}`} data-testid={`nav-${item.key}`}>
                      {t.nav[item.key]}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={dropdownTriggerClass(isActive('about'))}
                  data-testid="nav-more"
                  type="button"
                >
                  {t.nav.more ?? (language === 'pt' ? 'Mais' : 'More')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-slate-950 border-slate-800 text-slate-100"
              >
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={`/${language}/about`} data-testid="nav-about">
                    {t.nav.about}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-slate-800" />

                <DropdownMenuItem asChild className="cursor-pointer">
                  <a
                    href={moreNavItems.find((i) => i.key === 'help')?.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-help"
                  >
                    {t.nav.help}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a
                    href={moreNavItems.find((i) => i.key === 'status')?.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-status"
                  >
                    {t.nav.status}
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 rounded-lg transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language === 'pt' ? 'EN' : 'PT'}</span>
            </button>

            {/* CTA Button - Desktop */}
            <Link to={`/${language}/contact`} className="hidden lg:block">
              <Button 
                className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full px-6"
                data-testid="header-cta"
              >
                {t.home?.heroCta ?? t.common.contactUs}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-400 hover:text-slate-100"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-slate-800/50">
            <div className="flex flex-col gap-1">
              {mainNavItems.map((item) => (
                <Link
                  key={item.key}
                  to={`/${language}/${item.path}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-cyan-400 bg-cyan-400/10'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t.nav[item.key]}
                </Link>
              ))}

              <div className="my-2 border-t border-slate-800/50" />
              <p className="px-4 pt-2 pb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                {t.nav.content ?? (language === 'pt' ? 'Conteúdo' : 'Content')}
              </p>
              {contentNavItems.map((item) => (
                <Link
                  key={item.key}
                  to={`/${language}/${item.path}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-cyan-400 bg-cyan-400/10'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t.nav[item.key]}
                </Link>
              ))}

              <div className="my-2 border-t border-slate-800/50" />
              <p className="px-4 pt-2 pb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                {t.nav.more ?? (language === 'pt' ? 'Mais' : 'More')}
              </p>
              <Link
                to={`/${language}/about`}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                  isActive('about')
                    ? 'text-cyan-400 bg-cyan-400/10'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
                }`}
                data-testid="mobile-nav-about"
              >
                {t.nav.about}
              </Link>
              <a
                href={moreNavItems.find((i) => i.key === 'help')?.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium rounded-lg transition-colors text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                data-testid="mobile-nav-help"
              >
                {t.nav.help}
              </a>
              <a
                href={moreNavItems.find((i) => i.key === 'status')?.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium rounded-lg transition-colors text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                data-testid="mobile-nav-status"
              >
                {t.nav.status}
              </a>

              <Link
                to={`/${language}/contact`}
                onClick={() => setMobileMenuOpen(false)}
                className="mt-4"
              >
                <Button 
                  className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full"
                  data-testid="mobile-header-cta"
                >
                  {t.home?.heroCta ?? t.common.contactUs}
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
